import { useQuery } from '@tanstack/react-query';
import { identity } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Card, Stack, Title } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../i18n/I18nProvider';
import { AgreementForm, AGREEMENTS_KEY } from './AgreementForm';

/**
 * The console is not used until the organisation has accepted the current
 * data processing agreement (owner's decision, 2 October 2026: before
 * launch, recorded with who, when and which version). Covers organisations
 * that signed up before it existed, people who skipped onboarding, and every
 * organisation again when a new version is published.
 *
 * An operator looking in through impersonation sees a notice instead of the
 * gate: they may not accept for the customer, and must still be able to look.
 * If the question cannot be answered (identity down), the console is not
 * blocked: a missing answer is not a refusal.
 */
export function AgreementGate({ children }: { children: React.ReactNode }) {
  const t = useT();
  const { isImpersonating } = useAuth();
  const { data } = useQuery({
    queryKey: AGREEMENTS_KEY,
    queryFn: () => identity.listAgreements(),
    select: (r) => r.data,
    staleTime: 5 * 60_000
  });
  const due = (data ?? []).filter((a) => !a.accepted);
  if (due.length === 0) return <>{children}</>;

  if (isImpersonating)
    return (
      <>
        <div style={{ marginBottom: 16 }}>
          <Alert color="yellow" variant="light">{t('agreements.impersonatingBanner')}</Alert>
        </div>
        {children}
      </>
    );

  const first = due[0];
  return (
    <div className="rr-agreement-gate" role="dialog" aria-modal="true" aria-labelledby="rr-agreement-title"
      style={{ display: 'flex', justifyContent: 'center', padding: '48px 16px' }}>
      <Card withBorder style={{ maxWidth: 640, width: '100%' }}>
        <Stack gap="md">
          <Title order={2} id="rr-agreement-title">
            {first.accepted_version ? t('agreements.dpa.titleUpdated') : t('agreements.dpa.title')}
          </Title>
          <AgreementForm agreement={first} />
        </Stack>
      </Card>
    </div>
  );
}
